import React, { useState, useEffect } from "react";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { FaHistory, FaArrowUp, FaArrowDown, FaExclamationTriangle, FaTag } from "react-icons/fa";

const ProductTransactionHistory = ({ productId, inventoryId, product }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalIn: 0,
    totalOut: 0,
    currentStock: 0,
  });

  useEffect(() => {
    if (productId) {
      fetchTransactionHistory();
    }
  }, [productId, inventoryId]); 

  const fetchTransactionHistory = async () => {
    setLoading(true);
    try {
      // ✅ FIX: Pass inventoryId to get transactions for the specific inventory record
      const url = inventoryId 
        ? `/vendorproduct/transaction-history/${productId}?inventoryId=${inventoryId}`
        : `/vendorproduct/transaction-history/${productId}`;
        
      const response = await httpService.get(url);
      if (response.code === 200) {
        const transactions = response.data;
        setTransactions(transactions);

        const totalIn = transactions
          .filter((t) => t.quantity_change > 0)
          .reduce((sum, t) => sum + (t.quantity_change || 0), 0);

        const totalOut = transactions
          .filter((t) => t.quantity_change < 0)
          .reduce((sum, t) => sum + Math.abs(t.quantity_change || 0), 0);

        const currentStock = transactions.length > 0
          ? transactions[0].quantity_available
          : 0;

        setSummary({ totalIn, totalOut, currentStock });
      }
    } catch (err) {
      console.error("Error fetching transaction history:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "-"; 
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getTransactionTypeLabel = (type) => {
    const types = {
      'PURCHASE': 'Purchase',
      'SALE': 'Sale',
      'RETURN': 'Return',
      'WRITE_OFF': 'Write Off',
      'PRODUCT_CREATION': 'Product Creation',
      'EXPIRY': 'Expiry',
      'TRANSFER': 'Inventory Transfer',
      'PURCHASE_REVERSED': 'Purchase Reversed',
      'PRICE_MODIFIED': 'Price Modified',
      'PRODUCT_ADDED': 'Product Added',
      'STAFF_ASSIGNMENT': 'Staff Assignment',
      'STAFF_RETURN': 'Staff Return',
      'ADJUSTMENT': 'Adjustment',
    };
    return types[type] || type || '-';
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      'PURCHASE': 'bg-green-100 text-green-800',
      'SALE': 'bg-blue-100 text-blue-800',
      'RETURN': 'bg-orange-100 text-orange-800',
      'WRITE_OFF': 'bg-red-100 text-red-800',
      'PRODUCT_CREATION': 'bg-purple-100 text-purple-800',
      'EXPIRY': 'bg-yellow-100 text-yellow-800',
      'TRANSFER': 'bg-indigo-100 text-indigo-800',
      'PURCHASE_REVERSED': 'bg-pink-100 text-pink-800',
      'PRICE_MODIFIED': 'bg-gray-100 text-gray-800',
      'PRODUCT_ADDED': 'bg-teal-100 text-teal-800',
      'ADJUSTMENT': 'bg-red-100 text-red-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatShortDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  };

  const getBatchDisplay = (transaction) => {
    if (transaction.batch_details && transaction.batch_details.length > 0) {
      return transaction.batch_details
        .map((b) => {
          const exp = formatShortDate(b.expiry_date);
          return exp
            ? `${b.batch_number} (exp ${exp})`
            : `${b.batch_number}`;
        })
        .join(', ');
    }
    if (transaction.batch_number) {
      return transaction.batch_number;
    }
    return '-';
  };

  // ── Helper to get batch count ──
  const getBatchCount = (transaction) => {
    if (transaction.batch_details && transaction.batch_details.length > 0) {
      return transaction.batch_details.length;
    }
    if (transaction.batch_number) {
      return 1;
    }
    return 0;
  };

  if (loading) return <LoaderSpiner text="Loading transaction history..." />;

  return (
    <div className="p-2 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">
          Transaction History
        </h2>
        {transactions.length > 0 && (
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
            {transactions.length} Records
          </span>
        )}
      </div>

      {/* Summary Cards */}
      {transactions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Stock In</p>
                <p className="text-2xl font-bold">{summary.totalIn}</p>
              </div>
              <FaArrowDown className="w-8 h-8 opacity-80" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Stock Out</p>
                <p className="text-2xl font-bold">{summary.totalOut}</p>
              </div>
              <FaArrowUp className="w-8 h-8 opacity-80" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Current Stock</p>
                <p className="text-2xl font-bold">{summary.currentStock}</p>
              </div>
              <FaHistory className="w-8 h-8 opacity-80" />
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {transactions.length === 0 ? (
        <div className="text-center py-12">
          <FaHistory className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">
            No transaction history found for this product.
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Stock movements will appear here
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qty Change
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock After
                </th>
                {/* ✅ NEW: Batch Number Column */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <FaTag className="w-3 h-3" />
                    Batch
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchase Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sale Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  MRP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((transaction, idx) => {
                const isLossMaking =
                  transaction.purchase_price > 0 &&
                  transaction.sale_price > 0 &&
                  transaction.purchase_price > transaction.sale_price;
                
                const batchDisplay = getBatchDisplay(transaction);
                const batchCount = getBatchCount(transaction);

                return (
                  <tr
                    key={idx}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getTransactionTypeColor(transaction.transaction_type)}`}
                      >
                        {getTransactionTypeLabel(transaction.transaction_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`font-semibold ${
                          transaction.quantity_change > 0
                            ? "text-green-600"
                            : transaction.quantity_change < 0
                            ? "text-red-600"
                            : "text-gray-600"
                        }`}
                      >
                        {transaction.quantity_change > 0 ? "+" : ""}
                        {transaction.quantity_change}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {transaction.quantity_available}
                    </td>
                    {/* ✅ NEW: Batch Number Cell */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {batchDisplay !== '-' ? (
                        <div className="flex items-center gap-1.5">
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                            {batchDisplay}
                          </span>
                          {batchCount > 1 && (
                            <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold">
                              {batchCount}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-1">
                        <span className={`${isLossMaking ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                          {formatCurrency(transaction.purchase_price)}
                        </span>
                        {isLossMaking && (
                          <FaExclamationTriangle
                            className="text-red-500 w-4 h-4"
                            title="Purchase price higher than sale price"
                          />
                        )}
                      </div>
                      {isLossMaking && (
                        <span className="text-xs text-red-500 block">
                          Loss: {formatCurrency(transaction.purchase_price - transaction.sale_price)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(transaction.sale_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(transaction.mrp || product?.mrp)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                      <div className="relative group">
                        <span className="truncate block max-w-xs cursor-pointer">
                          {transaction.notes || transaction.reason || "-"}
                        </span>
                        {(transaction.notes || transaction.reason) && (
                          <div className="absolute z-50 hidden group-hover:block bottom-full left-0 mb-2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg whitespace-normal break-words">
                            {transaction.notes || transaction.reason}
                            <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900" />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductTransactionHistory;